import { useState, useEffect } from 'react'
import { format, parseISO } from 'date-fns'
// import { httpsCallable, getFunctions } from 'firebase/functions'
import Button from './Button'
import { computeFunnelByCustomer, formatPercent } from '../hooks/useGetAnalytics/funnel'

const EndpointList = ({ listData, detailData, timeFrame, apiKey, environment, keyNames = {}, isAdmin = false }) => {
  const [interval, setInterval] = useState()
  //   const [listData, setListData] = useState()

  // Helper function to escape CSV values
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) return ''
    const stringValue = String(value)
    // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`
    }
    return stringValue
  }

  const exportCSV = async () => {
    const previousDate = format(parseISO(timeFrame.previous), 'dd MMM yy hh:mm:ss')
    const todayDate = format(parseISO(timeFrame.today), 'dd MMM yy hh:mm:ss')
    const dataList = [listData]
    const keys = Object.keys(dataList[0])
    const values = dataList.map(item => Object.values(item))
    const rows = keys.map((key, index) => [key, ...values.map(arr => arr[index]), previousDate, todayDate])
    rows.unshift(['Endpoint', 'Hits', 'From', 'To'])

    const csv = rows.map(row => row.join(',')).join('\n')

    const blob = new Blob([csv], { type: 'application/vnd.ms-excel' })
    const url = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    const keyName = apiKey?.name || apiKey?.id || 'unknown'
    downloadLink.download = `summary_${keyName}_${environment}_${previousDate.replace(/\s+/g, '')}-${todayDate.replace(/\s+/g, '')}.csv`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
  }

  const exportDetail = async () => {
    const previousDate = format(parseISO(timeFrame.previous), 'dd MMM yy hh:mm:ss')
    const todayDate = format(parseISO(timeFrame.today), 'dd MMM yy hh:mm:ss')

    // Create header row from keys
    const keys = Object.keys(detailData[0])
    const rows = [keys.map(escapeCSVValue)]

    // Add each data item as a row
    detailData.forEach(item => {
      rows.push(Object.values(item).map(escapeCSVValue))
    })

    const csv = rows.map(row => row.join(',')).join('\n')

    const blob = new Blob([csv], { type: 'application/vnd.ms-excel' })
    const url = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    const keyName = apiKey?.name || apiKey?.id || 'unknown'
    downloadLink.download = `allTransactions_${keyName}_${environment}_${previousDate.replace(/\s+/g, '')}-${todayDate.replace(/\s+/g, '')}.csv`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
  }

  const exportBillables = async () => {
    const previousDate = format(parseISO(timeFrame.previous), 'dd MMM yy hh:mm:ss')
    const todayDate = format(parseISO(timeFrame.today), 'dd MMM yy hh:mm:ss')

    // Filter for billable endpoint records
    const billableData = detailData.filter(item => item.endpoint === '/ENGINE-NEW' || item.endpoint === '/PROVIDER-NEW')

    if (billableData.length === 0) {
      window.alert('No billable records found')
      return
    }

    // Create header row from keys
    const keys = Object.keys(billableData[0])
    const rows = [keys.map(escapeCSVValue)]

    // Add each data item as a row
    billableData.forEach(item => {
      rows.push(Object.values(item).map(escapeCSVValue))
    })

    const csv = rows.map(row => row.join(',')).join('\n')

    const blob = new Blob([csv], { type: 'application/vnd.ms-excel' })
    const url = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    const keyName = apiKey?.name || apiKey?.id || 'unknown'
    downloadLink.download = `billables_${keyName}_${environment}_${previousDate.replace(/\s+/g, '')}-${todayDate.replace(/\s+/g, '')}.csv`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
  }

  const exportFunnel = async () => {
    const previousDate = format(parseISO(timeFrame.previous), 'dd MMM yy hh:mm:ss')
    const todayDate = format(parseISO(timeFrame.today), 'dd MMM yy hh:mm:ss')

    const { overall, byCustomer } = computeFunnelByCustomer(detailData)

    if (overall.totalSessions === 0) {
      window.alert('No contact sessions found for this period')
      return
    }

    const rows = [
      ['Funnel Completion Report'],
      ['Reporting Period', `${previousDate} - ${todayDate}`],
      ['Total Unique Contacts (all customers)', overall.totalSessions],
      []
    ]

    // A per-customer section for each API key present in the data. Resolve the
    // raw customerKeyId to its human-readable name where we have it.
    byCustomer.forEach(({ customerKeyId, totalSessions, steps }) => {
      const keyName = keyNames[customerKeyId] || apiKey?.name || customerKeyId
      rows.push(['Customer', escapeCSVValue(keyName)])
      rows.push(['API Key ID', escapeCSVValue(customerKeyId)])
      rows.push(['Unique Contacts', totalSessions])
      rows.push(['Step', 'Count', 'Completion %'])
      steps.forEach((step) => {
        rows.push([escapeCSVValue(step.label), step.count, formatPercent(step.percent)])
      })
      rows.push([])
    })

    // Overall summary across all customers.
    rows.push(['Overall (all customers)'])
    rows.push(['Unique Contacts', overall.totalSessions])
    rows.push(['Step', 'Count', 'Completion %'])
    overall.steps.forEach((step) => {
      rows.push([escapeCSVValue(step.label), step.count, formatPercent(step.percent)])
    })

    const csv = rows.map((row) => row.join(',')).join('\n')

    const blob = new Blob([csv], { type: 'application/vnd.ms-excel' })
    const url = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    const keyName = apiKey?.name || apiKey?.id || 'unknown'
    downloadLink.download = `funnel_${keyName}_${environment}_${previousDate.replace(/\s+/g, '')}-${todayDate.replace(/\s+/g, '')}.csv`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    const previousDate = format(parseISO(timeFrame.previous), 'MMM dd, yyyy')
    const todayDate = format(parseISO(timeFrame.today), 'MMM dd, yyyy')
    setInterval({ today: todayDate, previous: previousDate })
  }, [timeFrame])

  if (interval && listData) {
    const totalHits = Object.values(listData).reduce((a, b) => a + b, 0)
    return (
      <div className='flex flex-row justify-around'>
        <div className='w-2/3 ml-2 my-4 text-left'>
          <div className='text-sm text-gray-600 mb-3 bg-gray-50 border rounded p-3 text-left'>
            <p className='mb-2'>Most data below represent API calls of any method type to that endpoint. The following events also appear in the report below:</p>
            <ul className='list-disc ml-5 space-y-1'>
              <li>ENGINE-NEW: a Predict Plans engine execution for a unique individual (determined by age, health conditions, medications and fips code)</li>
              <li>ENGINE-REPEAT: a Predict Plans engine execution for a repeat individual (not billable)</li>
              <li>PROVIDER-NEW: counted each time a Predict Plans engine execution includes matching Providers to Plans (Provider Plus customers only)</li>
            </ul>
          </div>
          <h2 className='text-xl font-bold mb-2'>
            {interval.previous} — {interval.today}
          </h2>
          <table className='bg-slate-50 table-auto w-full'>
            <thead className='bg-blue-4 rounded-md border-slate-50 border-b-4'>
              <tr>
                <td className='p-2 text-left'>Method</td>
                <td className='p-2'>Hits</td>
              </tr>
            </thead>
            <tbody>
              <tr className='border-blue-5 border-b-4'>
                <td className='p-2 text-left'>Total</td>
                <td className='p-2'>{totalHits}</td>
              </tr>
              {Object.keys(listData).map((key) => {
                if (key !== 'totalHits' && key !== 'sumHits') {
                  return (
                    <tr key={key} className='border-slate-50 border-b-4'>
                      <td className='p-2 text-left'>{key}</td>
                      <td className='p-2'>{listData[key]}</td>
                    </tr>
                  )
                } else {
                  return null
                }
              })}
            </tbody>
          </table>
        </div>
        <div className='flex flex-col gap-2'>
          <h3 className='text-lg font-semibold'>Exports</h3>
          <Button onClick={exportCSV} className='h-16'>Summary</Button>
          <Button onClick={exportDetail} className='h-16'>All Transactions</Button>
          <Button onClick={exportBillables} className='h-16'>Billables Only</Button>
          {isAdmin && <Button onClick={exportFunnel} className='h-16'>Funnel Completion</Button>}
        </div>
      </div>
    )
  } else {
    return null
  }
}

export default EndpointList
